/** Pure geometry helpers of the area editor (unit-tested; no map dependency). */
export type Pos = [number, number];
/** One polygon: outer ring first, optional holes; rings are closed (first == last). */
export type Poly = Pos[][];

/** Ramer–Douglas–Peucker on screen points (px), returns the kept indices' points. */
export function simplify<T extends { x: number; y: number }>(pts: T[], tolerance: number): T[] {
  if (pts.length <= 2) return pts.slice();
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const pa = pts[a]!;
    const pb = pts[b]!;
    const dx = pb.x - pa.x;
    const dy = pb.y - pa.y;
    const len = Math.hypot(dx, dy);
    let max = -1;
    let idx = -1;
    for (let i = a + 1; i < b; i++) {
      const p = pts[i]!;
      // Closed strokes start and end on the same point: fall back to the distance from that point.
      const d =
        len === 0
          ? Math.hypot(p.x - pa.x, p.y - pa.y)
          : Math.abs(dy * p.x - dx * p.y + pb.x * pa.y - pb.y * pa.x) / len;
      if (d > max) {
        max = d;
        idx = i;
      }
    }
    if (max > tolerance && idx > 0) {
      keep[idx] = 1;
      stack.push([a, idx], [idx, b]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

/** Closes a ring (adds the first point at the end if needed). Returns null for fewer than 3 distinct points. */
export function closeRing(points: Pos[]): Pos[] | null {
  const pts = points.filter((p, i) => i === 0 || p[0] !== points[i - 1]![0] || p[1] !== points[i - 1]![1]);
  if (pts.length > 1 && pts[0]![0] === pts.at(-1)![0] && pts[0]![1] === pts.at(-1)![1]) pts.pop();
  if (pts.length < 3) return null;
  return [...pts, pts[0]!];
}

export function toGeometry(parts: Poly[]): { type: 'MultiPolygon'; coordinates: Pos[][][] } | null {
  return parts.length ? { type: 'MultiPolygon', coordinates: parts } : null;
}

/** Accepts the stored MultiPolygon (or a Polygon) and returns editable parts. */
export function fromGeometry(g: { type: string; coordinates: unknown } | null | undefined): Poly[] {
  if (!g) return [];
  if (g.type === 'Polygon') return [g.coordinates as Poly];
  if (g.type === 'MultiPolygon') return g.coordinates as Poly[];
  return [];
}

/** Bounding box [[minLon,minLat],[maxLon,maxLat]] of the parts, or null. */
export function bounds(parts: Poly[]): [[number, number], [number, number]] | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const poly of parts)
    for (const ring of poly)
      for (const [x, y] of ring) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
  return Number.isFinite(minX)
    ? [
        [minX, minY],
        [maxX, maxY],
      ]
    : null;
}

/** Moves vertex i of ring r of part p, keeping the ring closed when the first/last vertex moves. */
export function moveVertex(parts: Poly[], p: number, r: number, i: number, to: Pos): Poly[] {
  return parts.map((poly, pi) =>
    pi !== p
      ? poly
      : poly.map((ring, ri) => {
          if (ri !== r) return ring;
          const last = ring.length - 1;
          return ring.map((pt, k) =>
            k === i || (i === 0 && k === last) || (i === last && k === 0) ? to : pt,
          );
        }),
  );
}
